import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { serverSideTableService } from "@/services/serverSideTableService";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
  useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X, ArrowUpDown, ArrowUp, ArrowDown, Settings2, Filter, Loader2, RefreshCw, Download, FileSpreadsheet, Printer, ArrowLeftRight, Banknote, Smartphone, CreditCard, Building2, ArrowLeft, ChevronDown, MoreHorizontal, Ban } from "lucide-react";
// xlsx loaded dynamically on export to reduce bundle size
import { toast } from "react-hot-toast";
import Filters from "./Filters";
import Pagination from "./Pagination";
import ReprintReceiptModal from "../POS/Modals/ReprintReceiptModal";
import billService from "../../services/billService";
import { paymentAccountsService } from "../../services/paymentAccountsService";
import { useAuth } from "../../context/AuthContext";
import { P } from "../../config/permissionCodes";

function SortableHeader({ id, children, isSorted, sortDir, onSort, align = 'left' }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const handleSortClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      onSort();
    }
  };

  const alignmentClass = align === 'right' ? 'text-right' : 'text-left';
  const justifyClass = align === 'right' ? 'justify-end' : 'justify-start';

  return (
    <th
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`px-4 py-3 bg-gradient-to-b from-gray-50 to-gray-100 ${alignmentClass} text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200`}
    >
      <div className={`flex items-center gap-2 select-none ${justifyClass}`}>
        <span {...listeners} className="cursor-grab">{children}</span>
        <button
          type="button"
          onClick={handleSortClick}
          className={`p-1 rounded hover:bg-gray-200 cursor-pointer transition-colors ${isSorted ? 'text-teal-600' : 'text-gray-400'}`}
        >
          {isSorted ? (sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} />}
        </button>
      </div>
    </th>
  );
}

// Row-level Actions dropdown (3-dot button) — RBAC-aware
function RowActions({ row, onCancelBill, canCancelBill }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClose = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClose);
    return () => document.removeEventListener('mousedown', handleClose);
  }, [open]);

  const toggleOpen = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.right - 160, 
      });
    }
    setOpen(v => !v);
  };
  const hasAnyAction = canCancelBill;
  if (!hasAnyAction) {
    return <span className="text-gray-300 text-sm">—</span>;
  }

  return (
    <div className="relative flex justify-center">
      <button
        ref={btnRef}
        onClick={toggleOpen}
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        title="Actions"
      >
        <MoreHorizontal size={16} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
          className="bg-white border border-gray-200 rounded-lg shadow-xl z-[100] min-w-[160px] py-1"
        >
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
            Bill #{row?.bill_number || row?.billNumber || '—'}
          </div>
          {canCancelBill && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                if (onCancelBill) onCancelBill(row);
                else toast.error('Cancel bill handler not configured');
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <Ban size={14} />
              Cancel Bill
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default function ServerSideTable({ tableName, defaultColumns, dataUrl, additionalFilters = {}, defaultFilters = {}, canExport = true, canChangePaymentMode = true, canReprint = true, onCancelBill }) {
  // ─── RBAC ────────────────────────────────────────────────────────────────
  // Cancel-bill uses the existing POS.BILL.VOID permission ("Void Bill") from
  // the permission catalog. The back-office bills page reuses this permission
  // since cancelling a finalized bill is conceptually the same destructive
  // action — voiding it.
  const { hasPermission } = useAuth();
  const canCancelBill = hasPermission(P.BACK_OFFICE.BILLS.CANCEL);

  // State
  const [allColumns, setAllColumns] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalSelected, setModalSelected] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('finalized_at'); // Default sort by finalized date
  const [sortDir, setSortDir] = useState("desc"); // Latest first
  const [filters, setFilters] = useState(defaultFilters);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [billItems, setBillItems] = useState([]);
  const [billItemsLoading, setBillItemsLoading] = useState(false);
  const [billPayments, setBillPayments] = useState([]);
  const [billPaymentsLoading, setBillPaymentsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showReprintModal, setShowReprintModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  // Close export menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);
  const [showPaymentModeModal, setShowPaymentModeModal] = useState(false);
  const [paymentModeUpdating, setPaymentModeUpdating] = useState(false);
  // Two-step payment-mode modal: 'mode' = pick Cash/UPI/Card/Bank,
  // 'account' = pick a specific account for the chosen (non-cash) mode.
  const [pmStep, setPmStep] = useState('mode');
  const [pmChosenMode, setPmChosenMode] = useState(null);
  const [pmAccounts, setPmAccounts] = useState([]);
  const [pmAccountsLoading, setPmAccountsLoading] = useState(false);

  // Cancel-bill confirmation modal
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [billToCancel, setBillToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Refs
  const layoutLoaded = useRef(false);
  const fetchCounter = useRef(0);

  // Memoize default columns
  const stableDefaultColumns = useMemo(() => defaultColumns || [], []);

  // Fetch the payment rows (mode + linked account) for a bill
  const loadBillPayments = useCallback(async (billId) => {
    setBillPaymentsLoading(true);
    try {
      const res = await serverSideTableService.fetchBillPayments(billId);
      const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setBillPayments(data);
    } catch (err) {
      console.error("Failed to fetch bill payments:", err);
      setBillPayments([]);
    } finally {
      setBillPaymentsLoading(false);
    }
  }, []);

  // Fetch bill items + payments
  const handleBillClick = useCallback(async (bill) => {
    setSelectedBill(bill);
    setBillModalOpen(true);
    setBillItemsLoading(true);
    setBillItems([]);
    setBillPayments([]);
    loadBillPayments(bill.id);
    try {
      const items = await serverSideTableService.fetchBillItems(bill.id);
      setBillItems(items || []);
    } catch (err) {
      console.error("Failed to fetch bill items:", err);
    } finally {
      setBillItemsLoading(false);
    }
  }, [loadBillPayments]);

  // Reset + close the payment-mode modal back to a clean state
  const closePaymentModeModal = useCallback(() => {
    setShowPaymentModeModal(false);
    setPmStep('mode');
    setPmChosenMode(null);
    setPmAccounts([]);
    setPmAccountsLoading(false);
  }, []);

  // Payment mode change handler. For Cash, accountId stays null. For
  // UPI/Card/Bank a specific account is chosen; displayLabel is its name so
  // the table row mirrors the server's COALESCE(account_name, mode) display.
  const handlePaymentModeChange = useCallback(async (newMode, accountId = null, displayLabel = null) => {
    if (!selectedBill?.id) return;
    setPaymentModeUpdating(true);
    try {
      await billService.updatePaymentMode(selectedBill.id, newMode, accountId);
      const shown = displayLabel || newMode;
      toast.success(`Payment mode changed to ${shown}`);
      // Optimistic local update (server display = account name when set)
      setSelectedBill(prev => ({ ...prev, payment_modes: shown, paymentMode: shown }));
      setRows(prev => prev.map(row =>
        row.id === selectedBill.id ? { ...row, payment_modes: shown, paymentMode: shown } : row
      ));
      closePaymentModeModal();
      // Keep the Payment Details section in sync with the new mode/account
      loadBillPayments(selectedBill.id);
    } catch (err) {
      console.error("Failed to update payment mode:", err);
      toast.error("Failed to update payment mode");
    } finally {
      setPaymentModeUpdating(false);
    }
  }, [selectedBill, closePaymentModeModal, loadBillPayments]);

  // Step 1 tile click: Cash applies immediately; others load their accounts
  const handleModeTileClick = useCallback(async (modeDef) => {
    if (paymentModeUpdating) return;
    if (modeDef.mode === 'CASH') {
      handlePaymentModeChange('CASH');
      return;
    }
    setPmChosenMode(modeDef);
    setPmStep('account');
    setPmAccountsLoading(true);
    setPmAccounts([]);
    try {
      const response = await paymentAccountsService.getAll({
        accountType: modeDef.accountType,
        isActive: true,
      });
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      // Defensive: keep only active accounts of the requested type
      setPmAccounts(
        data.filter(
          (a) =>
            (a.accountType || a.account_type) === modeDef.accountType &&
            (a.isActive ?? a.is_active ?? true)
        )
      );
    } catch (err) {
      console.error("Failed to fetch payment accounts:", err);
      toast.error("Failed to load accounts");
      setPmAccounts([]);
    } finally {
      setPmAccountsLoading(false);
    }
  }, [paymentModeUpdating, handlePaymentModeChange]);

  // Secondary line shown under each account in the picker
  const accountSubtitle = useCallback((acc) => {
    const type = acc.accountType || acc.account_type;
    if (type === 'upi') {
      return [acc.upiId || acc.upi_id, acc.paymentGateway || acc.payment_gateway]
        .filter(Boolean)
        .join(' · ');
    }
    if (type === 'bank') {
      return [acc.bankName || acc.bank_name, acc.accountNumber || acc.account_number]
        .filter(Boolean)
        .join(' · ');
    }
    if (type === 'card_machine') {
      return [acc.machineProvider || acc.machine_provider, acc.terminalId || acc.terminal_id]
        .filter(Boolean)
        .join(' · ');
    }
    return '';
  }, []);

  // Build bill object for ReprintReceiptModal
  const buildBillForReprint = useCallback(() => {
    if (!selectedBill || billItems.length === 0) return null;
    return {
      ...selectedBill,
      billNumber: selectedBill.bill_number || selectedBill.billNumber,
      tableName: (selectedBill.table_number || selectedBill.tableNumber) === 'MAVERICK_COUNTER'
        ? 'Takeaway'
        : (selectedBill.table_number || selectedBill.tableNumber),
      paymentMode: selectedBill.payment_modes || selectedBill.paymentMode,
      orders: [{
        items: billItems.map(item => ({
          itemName: item.itemName,
          variantName: item.variantName,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        }))
      }]
    };
  }, [selectedBill, billItems]);

  // Fetch layout once
  useEffect(() => {
    if (!tableName || layoutLoaded.current) return;

    async function loadLayout() {
      try {
        const resp = await serverSideTableService.fetchTableLayout(tableName);
        if (resp?.columns) {
          setAllColumns(resp.columns.filter(col => col.key !== "id"));
        }
        if (resp?.selectedColumns) {
          const keys = resp.selectedColumns.map(c => c.key).filter(key => key !== "id");

          // Always include recipient_phone if it exists in allColumns
          const hasRecipient = resp.columns.some(c => c.key === "recipient_phone");
          let finalKeys = hasRecipient && !keys.includes("recipient_phone")
            ? [...keys, "recipient_phone"]
            : keys;

          // Include __actions by default (virtual column, toggleable in modal).
          // The header/cell render itself is RBAC-gated below, so even if this
          // sticks around for a user without permission, it's invisible.
          if (!finalKeys.includes("__actions")) finalKeys = [...finalKeys, "__actions"];

          setVisibleColumns(finalKeys);
          setModalSelected(finalKeys);
        } else if (stableDefaultColumns.length) {
          const keys = stableDefaultColumns.filter(key => key !== "id");

          const hasRecipient = resp?.columns?.some(c => c.key === "recipient_phone");
          let finalKeys = hasRecipient && !keys.includes("recipient_phone")
            ? [...keys, "recipient_phone"]
            : keys;

          // Same note as above re: __actions visibility being RBAC-gated.
          if (!finalKeys.includes("__actions")) finalKeys = [...finalKeys, "__actions"];

          setVisibleColumns(finalKeys);
          setModalSelected(finalKeys);
        }
        layoutLoaded.current = true;
      } catch (err) {
        console.error("Layout error:", err);
      }
    }
    loadLayout();
  }, [tableName, stableDefaultColumns]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    if (!dataUrl) return;

    const currentFetch = ++fetchCounter.current;
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: page - 1,
        size: pageSize,
        sort: [`${sortBy},${sortDir}`],
      };

      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params[key] = value;
        }
      });

      // Add additional filters from parent
      Object.entries(additionalFilters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params[key] = value;
        }
      });

      const data = await serverSideTableService.fetchTableData(dataUrl, params);

      if (currentFetch === fetchCounter.current) {
        setRows(data.content || []);
        setTotalRows(data.totalElements || 0);
        setTotalPages(Math.max(1, Math.ceil((data.totalElements || 0) / pageSize)));
      }
    } catch (err) {
      if (currentFetch === fetchCounter.current) {
        setError(err.message || String(err));
      }
    } finally {
      if (currentFetch === fetchCounter.current) {
        setLoading(false);
      }
    }
  }, [dataUrl, page, pageSize, sortBy, sortDir, filters, additionalFilters]);

  // Fetch data on dependency change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers
  const handleSort = useCallback((colKey) => {
    if (sortBy === colKey) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(colKey);
      setSortDir("desc");
    }
    setPage(1);
  }, [sortBy]);

  const handleFilterApply = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilters(defaultFilters);
    setPage(1);
  }, [defaultFilters]);

  const handleRefresh = useCallback(() => {
    fetchCounter.current++;
    fetchData();
  }, [fetchData]);

  // Cancel-bill flow: clicking Cancel Bill from the row menu opens a styled
  // confirmation modal. The actual API call happens in confirmCancelBill below.
  // Also gated by RBAC — refuse silently if user somehow gets here without perm.
  const handleCancelBill = useCallback((bill) => {
    if (!canCancelBill) {
      toast.error('You do not have permission to cancel bills');
      return;
    }
    if (!bill?.id) {
      toast.error('Bill ID missing');
      return;
    }
    setBillToCancel(bill);
    setCancelReason('');
    setCancelConfirmOpen(true);
  }, [canCancelBill]);

  const closeCancelConfirm = useCallback(() => {
    if (cancelling) return;
    setCancelConfirmOpen(false);
    setBillToCancel(null);
    setCancelReason('');
  }, [cancelling]);

  const confirmCancelBill = useCallback(async () => {
    if (!canCancelBill) {
      toast.error('You do not have permission to cancel bills');
      return;
    }
    if (!billToCancel?.id) return;
    // Mirrors the backend's own check — it rejects a blank reason with 400.
    const reason = cancelReason.trim();
    if (!reason) {
      toast.error('Please enter a reason for cancelling this bill');
      return;
    }
    const billLabel = billToCancel.bill_number || billToCancel.billNumber || billToCancel.id;
    setCancelling(true);
    try {
      await billService.cancelBill(billToCancel.id, reason);
      toast.success(`Bill #${billLabel} cancelled`);
      setCancelConfirmOpen(false);
      setBillToCancel(null);
      setCancelReason('');
      fetchCounter.current++;
      fetchData();
    } catch (err) {
      console.error('Failed to cancel bill:', err);
      toast.error(err?.response?.data?.message || 'Failed to cancel bill');
    } finally {
      setCancelling(false);
    }
  }, [billToCancel, cancelReason, fetchData, canCancelBill]);

  // Fetch all records for export (no pagination)
  const fetchAllForExport = useCallback(async () => {
    const params = {
      page: 0,
      size: 10000,
      sort: [`${sortBy},${sortDir}`],
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) params[key] = value;
    });
    Object.entries(additionalFilters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) params[key] = value;
    });
    const data = await serverSideTableService.fetchTableData(dataUrl, params);
    return data.content || [];
  }, [dataUrl, sortBy, sortDir, filters, additionalFilters]);

  // Get headers and row data for export
  const getExportData = useCallback((allRows) => {
    const exportableCols = visibleColumns.filter(k => k !== "__actions");
    const headers = exportableCols.map(key => {
      const col = allColumns.find(c => c.key === key);
      return col?.value || key;
    });
    const rowData = allRows.map(row =>
      exportableCols.map(key => {
        const val = row[key];
        return val === null || val === undefined ? '' : formatCellValue(val, key);
      })
    );
    return { headers, rowData };
  }, [visibleColumns, allColumns]);

  const fileName = `${tableName || 'export'}_${new Date().toISOString().split('T')[0]}`;

  const handleExport = useCallback(async (format = 'csv') => {
    if (!canExport || !dataUrl) return;
    setExporting(true);
    setShowExportMenu(false);
    try {
      const allRows = await fetchAllForExport();
      if (allRows.length === 0) {
        toast.error('No data to export');
        setExporting(false);
        return;
      }

      const { headers, rowData } = getExportData(allRows);

      if (format === 'csv') {
        const csvRows = [headers.join(',')];
        rowData.forEach(values => {
          csvRows.push(values.map(val => {
            val = String(val);
            if (val.includes(',') || val.includes('"') || val.includes('\n')) {
              return `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          }).join(','));
        });
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      if (format === 'excel') {
        const XLSX = await import('xlsx');
        const wsData = [headers, ...rowData];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = headers.map((h, i) => ({
          wch: Math.max(h.length, ...rowData.map(r => String(r[i] || '').length)) + 2
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Data');
        XLSX.writeFile(wb, `${fileName}.xlsx`);
      }

      toast.success(`Exported ${allRows.length} records as ${format.toUpperCase()}`);
    } catch (err) {
      console.error("Export error:", err);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  }, [canExport, dataUrl, fetchAllForExport, getExportData, fileName, tableName]);

  const toggleModalSelect = (colKey) => {
    setModalSelected(prev => prev.includes(colKey) ? prev.filter(k => k !== colKey) : [...prev, colKey]);
  };

  const applyModalSelection = () => {
    setVisibleColumns(modalSelected);
    setModalVisible(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setVisibleColumns(cols => arrayMove(cols, cols.indexOf(active.id), cols.indexOf(over.id)));
  };

  // Helpers
  const formatCellValue = (value, colKey) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value === 'MAVERICK_COUNTER') return 'Takeaway';

    // Format duration in minutes to human-readable format
    if (colKey.toLowerCase().includes('duration') && typeof value === 'number') {
      const hours = Math.floor(value / 60);
      const minutes = Math.round(value % 60);
      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else {
        return `${minutes}m`;
      }
    }

    // Format currency - exclude totalItems from currency formatting
    if ((colKey.includes('finalAmount')) || (colKey.includes('amount') || colKey.includes('price') || colKey.includes('subtotal') ||
         (colKey.includes('total') && !colKey.toLowerCase().includes('totalitems'))) &&
        typeof value === 'number') {
      return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    }
    if (colKey.includes('_at') || colKey.includes('date')) {
      try {
        return new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
      } catch { return value; }
    }
    return String(value);
  };

  const getStatusBadge = (status) => {
    const colors = {
      'paid': 'bg-teal-100 text-teal-700 border-teal-200',
      'PAID': 'bg-teal-100 text-teal-700 border-teal-200',
      'pending': 'bg-amber-100 text-amber-700 border-amber-200',
      'PENDING': 'bg-amber-100 text-amber-700 border-amber-200',
      'partial': 'bg-blue-100 text-blue-700 border-blue-200',
      'PARTIAL': 'bg-blue-100 text-blue-700 border-blue-200',
      'open': 'bg-blue-100 text-blue-700 border-blue-200',
      'closed': 'bg-gray-100 text-gray-600 border-gray-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '' && v !== null && v !== undefined).length;
  // +1 for the # column. Actions is already inside visibleColumns when enabled.
  const visibleCountWithSL = (visibleColumns?.length || 0) + 1;
  const realColumns = (allColumns.length ? allColumns : stableDefaultColumns.map(key => ({ key, value: key }))).filter(col => col.key !== "id");
  // Whether to expose the Actions column in the column chooser at all. Hidden
  // when the user has no row-level action permissions — no point showing a
  // toggle for an always-empty column.
  const hasAnyRowAction = canCancelBill;
  const modalColumns = hasAnyRowAction
    ? [...realColumns, { key: "__actions", value: "Actions" }]
    : realColumns;

  return (
    <div className="p-4 w-full max-w-full">
      {/* Header Bar */}
      <div className="mb-4 flex flex-wrap gap-3 justify-between items-center bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilterOpen(f => !f)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              filterOpen
                ? 'bg-teal-600 text-white shadow-md'
                : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'
            }`}
          >
            <Filter size={16} />
            <span>{filterOpen ? "Hide Filters" : "Filters"}</span>
            {activeFiltersCount > 0 && !filterOpen && (
              <span className="bg-teal-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full ml-1">
                {activeFiltersCount}
              </span>
            )}
          </button>

          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 text-sm font-medium transition-all disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {canExport && (
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(prev => !prev)}
              disabled={exporting || rows.length === 0}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 text-sm font-medium transition-all disabled:opacity-50"
            >
              {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              <span>Export</span>
              <ChevronDown size={14} />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px] py-1">
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FileSpreadsheet size={15} className="text-green-600" />
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExport('excel')}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FileSpreadsheet size={15} className="text-emerald-600" />
                  Export as Excel
                </button>
              </div>
            )}
          </div>
          )}
          <button
            onClick={() => { setModalSelected(visibleColumns); setModalVisible(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 text-sm font-medium transition-all shadow-md"
          >
            <Settings2 size={16} />
            <span>Columns</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {filterOpen && (
        <Filters initialValues={filters} onApply={handleFilterApply} onReset={handleFilterReset} />
      )}

      {/* Results Info */}
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {loading ? 'Loading...' : `Showing ${rows.length} of ${totalRows.toLocaleString()} bills`}
        </div>
        <div className="text-xs text-gray-400">
          Sorted by: <span className="font-medium text-gray-600">
            {allColumns.find(col => col.key === sortBy)?.value || sortBy.replace(/_/g, ' ')}
          </span> ({sortDir === 'desc' ? 'Latest first' : 'Oldest first'})
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="overflow-x-auto">
            <table className="w-full table-auto min-w-[800px]">
              <thead>
                <SortableContext items={visibleColumns} strategy={horizontalListSortingStrategy}>
                  <tr>
                    <th className="px-4 py-3 bg-gradient-to-b from-gray-50 to-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-14">#</th>
                    {visibleColumns.map(key => {
                      if (key === "id" || key === "__actions") return null;
                      const col = allColumns.find(c => c.key === key) || { key, value: key };
                      const columnLabel =
                      key === "billStatus" ? "Payment Status" : col.value;
                      // Use align from column config, or auto-detect for amount columns
                      const columnAlign = col.align ||
                                          ((key.includes('finalAmount') || key.includes('amount') || key.includes('price') ||
                                            key.includes('subtotal') ||
                                            (key.includes('total') && !key.toLowerCase().includes('totalitems')))
                                          ? 'right' : 'left');
                      return (
                        <SortableHeader
                          key={key}
                          id={key}
                          isSorted={sortBy === key}
                          sortDir={sortDir}
                          onSort={() => handleSort(key)}
                          align={columnAlign}
                        >
                          {columnLabel}
                        </SortableHeader>
                      );
                    })}
                    {/* Actions column header — fixed, not sortable/draggable; toggleable via column chooser; RBAC-gated */}
                    {canCancelBill && visibleColumns.includes("__actions") && (
                      <th className="px-4 py-3 bg-gradient-to-b from-gray-50 to-gray-100 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-20">
                        Actions
                      </th>
                    )}
                  </tr>
                </SortableContext>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={visibleCountWithSL} className="p-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 size={36} className="animate-spin text-teal-600" />
                        <span className="text-gray-500 font-medium">Loading bills...</span>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={visibleCountWithSL} className="p-16 text-center">
                      <div className="text-red-500 font-medium mb-2">Failed to load data</div>
                      <div className="text-gray-400 text-sm">{error}</div>
                      <button onClick={handleRefresh} className="mt-3 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm">
                        Try Again
                      </button>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={visibleCountWithSL} className="p-16 text-center">
                      <FileSpreadsheet size={48} className="mx-auto text-gray-300 mb-3" />
                      <div className="text-gray-500 font-medium">No bills found</div>
                      <div className="text-gray-400 text-sm mt-1">Try adjusting your filters</div>
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={r.id ?? idx} className="hover:bg-teal-50/40 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-500 font-medium">{(page - 1) * pageSize + idx + 1}</td>
                      {visibleColumns.map((colKey) => {
                        if (colKey === "id" || colKey === "__actions") return null; // Actions is rendered separately after this map
                        const col = allColumns.find(c => c.key === colKey) || { key: colKey, link: false };
                        const cellValue = r[colKey];

                        // Determine alignment from column config or auto-detect
                        const cellAlign = col.align ||
                                         ((colKey.includes('amount') || colKey.includes('price') ||
                                           colKey.includes('subtotal') ||
                                           (colKey.includes('total') && !colKey.toLowerCase().includes('totalitems')))
                                          ? 'right' : 'left');
                        const alignmentClass = cellAlign === 'right' ? 'text-right' : '';

                        // NC badge
                        if (colKey === 'isNc') {
                          return (
                            <td key={`${colKey}-${idx}`} className="px-4 py-3">
                              {cellValue ? (
                                <span className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border bg-amber-100 text-amber-700 border-amber-200">
                                  NC
                                </span>
                              ) : (
                                <span className="text-gray-400 text-sm">—</span>
                              )}
                            </td>
                          );
                        }

                        // Display "Takeaway" instead of "MAVERICK_COUNTER"
                        if (colKey === 'table_number' && cellValue === 'MAVERICK_COUNTER') {
                          return (
                            <td key={`${colKey}-${idx}`} className={`px-4 py-3 text-sm ${alignmentClass}`}>
                              Takeaway
                            </td>
                          );
                        }
                        // WhatsApp delivery method — show phone number inline
                        if (colKey === 'bill_delivery_method') {
                          const isWhatsApp = cellValue === 'WHATSAPP';
                          // Try both camelCase and snake_case
                          const rawPhone = r.recipientPhone || r.recipient_phone
                            ? String(r.recipientPhone || r.recipient_phone) : null;
                          return (
                            <td key={`${colKey}-${r.id ?? idx}`} className="px-4 py-3">
                              <div className="flex flex-col gap-0.5">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border w-fit ${
                                  isWhatsApp
                                    ? 'bg-green-50 text-green-700 border-green-200'
                                    : 'bg-gray-100 text-gray-600 border-gray-200'
                                }`}>
                                  {isWhatsApp && (
                                    <svg viewBox="0 0 24 24" className="w-3 h-3 fill-green-600" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                    </svg>
                                  )}
                                  {cellValue || '-'}
                                </span>
                                {isWhatsApp && rawPhone && (
                                  <span className="text-xs text-gray-500 pl-0.5">{rawPhone}</span>
                                )}
                              </div>
                            </td>
                          );
                        }

                        // Show recipient_phone only when delivery method is WhatsApp
                        if (colKey === 'recipient_phone') {
                          // Try both camelCase and snake_case for delivery method check
                          const isWhatsApp = (r.billDeliveryMethod || r.bill_delivery_method) === 'WHATSAPP';
                          // Try both for the phone value too
                          const phoneValue = cellValue || r.recipientPhone || r.recipient_phone;
                          if (!isWhatsApp || !phoneValue) {
                            return <td key={`${colKey}-${r.id ?? idx}`} className="px-4 py-3 text-sm text-gray-400">—</td>;
                          }
                          const rawPhone = String(phoneValue);
                          const formatted = rawPhone
                            .replace(/^91(\d{10})$/, '+91 $1')
                            .replace(/(\d{5})(\d{5})$/, '$1 $2');
                          return (
                            <td key={`${colKey}-${r.id ?? idx}`} className="px-4 py-3 text-sm text-gray-700">
                              {formatted}
                            </td>
                          );
                        }

                        if (colKey.includes('status')) {
                          return (
                            <td key={`${colKey}-${idx}`} className="px-4 py-3">
                              <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${getStatusBadge(cellValue)}`}>
                                {cellValue || '-'}
                              </span>
                            </td>
                          );
                        }

                        if (col.link || colKey === 'bill_number') {
                          return (
                            <td
                              key={`${colKey}-${idx}`}
                              className={`px-4 py-3 text-sm text-teal-600 font-semibold cursor-pointer hover:underline ${alignmentClass}`}
                              onClick={() => handleBillClick(r)}
                            >
                              {formatCellValue(cellValue, colKey)}
                            </td>
                          );
                        }

                        return (
                          <td key={`${colKey}-${idx}`} className={`px-4 py-3 text-sm text-gray-700 ${alignmentClass}`}>
                            {formatCellValue(cellValue, colKey)}
                          </td>
                        );
                      })}
                      {/* Actions cell — 3-dot dropdown; toggleable via column chooser; RBAC-gated */}
                      {canCancelBill && visibleColumns.includes("__actions") && (
                        <td className="px-4 py-3">
                          <RowActions
                            row={r}
                            onCancelBill={onCancelBill || handleCancelBill}
                            canCancelBill={canCancelBill}
                          />
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DndContext>
      </div>

      {/* Pagination */}
      <div className="mt-4 bg-white border border-gray-200 rounded-xl px-4 shadow-sm">
        <Pagination
          totalPages={totalPages}
          currentPage={page}
          onPageChange={setPage}
          totalItems={totalRows}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      </div>

      {/* Column Chooser Modal */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Configure Columns</h3>
              <button onClick={() => setModalVisible(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                <X size={20} className="text-white" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
              <p className="text-sm text-gray-500 mb-4">Select columns to display in the table</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
                {modalColumns.map((col) => (
                  <div
                    key={col.key}
                    onClick={() => toggleModalSelect(col.key)}
                    className={`px-3 py-2.5 rounded-lg cursor-pointer text-sm border-2 transition-all ${
                      modalSelected.includes(col.key)
                        ? "bg-teal-50 border-teal-500 text-teal-700 font-medium"
                        : "bg-gray-50 border-transparent text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    {col.value}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button onClick={() => setModalVisible(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  Cancel
                </button>
                <button onClick={applyModalSelection} className="px-5 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm">
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bill Items Modal */}
      {billModalOpen && selectedBill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-bold text-white">Bill # {selectedBill.bill_number || selectedBill.billNumber}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-teal-100 text-sm">
                      {(selectedBill.table_number || selectedBill.tableNumber) && (
                      (selectedBill.table_number || selectedBill.tableNumber) === 'MAVERICK_COUNTER'
                        ? 'Takeaway'
                        : `Table ${selectedBill.table_number || selectedBill.tableNumber}`
                    )}
                      {(selectedBill.customer_name || selectedBill.customerName) && ` • ${selectedBill.customer_name || selectedBill.customerName}`}
                    </p>
                    {(selectedBill.payment_modes || selectedBill.paymentMode) && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        (selectedBill.payment_modes || selectedBill.paymentMode || '').toUpperCase() === 'CASH'
                          ? 'bg-emerald-100 text-emerald-700'
                          : (selectedBill.payment_modes || selectedBill.paymentMode || '').toUpperCase() === 'UPI'
                            ? 'bg-violet-100 text-violet-700'
                            : 'bg-blue-100 text-blue-700'
                      }`}>
                        {selectedBill.payment_modes || selectedBill.paymentMode}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => setBillModalOpen(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                  <X size={20} className="text-white" />
                </button>
              </div>
            </div>

            {/* NC Banner */}
            {(selectedBill.isNc || selectedBill.is_nc) && (
              <div className="mx-4 mt-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                <span className="text-amber-600 font-semibold text-sm">NC Bill</span>
                {(selectedBill.ncReason || selectedBill.nc_reason) && (
                  <span className="text-amber-500 text-sm">— {selectedBill.ncReason || selectedBill.nc_reason}</span>
                )}
              </div>
            )}

            {/* Items List */}
            <div className="overflow-y-auto max-h-[calc(90vh-220px)]">
              {billItemsLoading ? (
                <div className="p-8 text-center">
                  <Loader2 size={32} className="animate-spin text-teal-600 mx-auto" />
                  <p className="text-gray-500 mt-2">Loading items...</p>
                </div>
              ) : billItems.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No items found</div>
              ) : (
                <div className="p-4">
                  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    {/* Table Header */}
                    <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600">
                      <div className="col-span-6">Item</div>
                      <div className="col-span-2 text-center">Qty</div>
                      <div className="col-span-2 text-right">Rate</div>
                      <div className="col-span-2 text-right">Amount</div>
                    </div>
                    {/* Items */}
                    <div className="divide-y divide-slate-100">
                      {billItems.map((item, idx) => (
                        <div key={item.id || idx} className="px-3 py-2 grid grid-cols-12 gap-2 text-sm hover:bg-slate-50">
                          <div className="col-span-6 font-medium text-slate-900">
                            <span className="truncate block">
                              {item.itemName}
                              {(item.isComp || item.is_comp) && (
                                <span className="text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded font-medium ml-1">NC</span>
                              )}
                            </span>
                            {item.variantName && (
                              <span className="text-xs text-teal-600">{item.variantName}</span>
                            )}
                          </div>
                          <div className="col-span-2 text-center text-slate-700">{item.quantity}</div>
                          <div className="col-span-2 text-right text-slate-600">₹{item.unitPrice}</div>
                          <div className="col-span-2 text-right font-semibold text-slate-900">₹{item.totalAmount}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Summary */}
            {!billItemsLoading && billItems.length > 0 && (
              <div className="border-t-2 border-slate-200 bg-white px-6 py-4 space-y-2">
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Subtotal ({billItems.length} items)</span>
                  <span className="font-semibold">₹{selectedBill.subtotal || billItems.reduce((sum, i) => sum + (i.totalAmount || 0), 0)}</span>
                </div>
                {selectedBill.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Discount</span>
                    <span className="text-red-600 font-semibold">-₹{selectedBill.discount_amount}</span>
                  </div>
                )}
                {(selectedBill.ncAmount > 0 || selectedBill.nc_amount > 0) && (
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>NC Deduction</span>
                    <span className="text-amber-600 font-semibold">-₹{(selectedBill.ncAmount || selectedBill.nc_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {selectedBill.tax_amount > 0 && (
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Tax</span>
                    <span className="font-semibold">₹{selectedBill.tax_amount}</span>
                  </div>
                )}
                <div className="border-t border-slate-200 pt-2"></div>
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg text-slate-900">Total</span>
                  <span className="text-2xl font-black text-teal-700">₹{selectedBill.totalAmount}</span>
                </div>
                {selectedBill.finalized_at && (
                  <div className="text-xs text-gray-400 text-right pt-1">
                    {new Date(selectedBill.finalized_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </div>
                )}

                {/* Payment Details */}
                <div className="pt-3 border-t border-slate-200 mt-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                    Payment Details
                  </h4>
                  {billPaymentsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
                      <Loader2 size={14} className="animate-spin" />
                      <span>Loading payments…</span>
                    </div>
                  ) : billPayments.length === 0 ? (
                    <p className="text-sm text-slate-400 py-1">No payment records.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {billPayments.map((p, idx) => {
                        const mode = (p.paymentMode || '').toUpperCase();
                        const label = p.accountName ? `${mode} · ${p.accountName}` : (mode || '—');
                        return (
                          <div
                            key={p.paymentNumber || idx}
                            className="flex items-center justify-between gap-3 text-sm bg-slate-50 rounded-lg px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate">{label}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {p.splitLabel && (
                                  <span className="text-[10px] font-semibold text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded">
                                    {p.splitLabel}
                                  </span>
                                )}
                                {p.paymentStatus && p.paymentStatus.toLowerCase() !== 'completed' && (
                                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded uppercase">
                                    {p.paymentStatus}
                                  </span>
                                )}
                                {p.paymentDate && (
                                  <span className="text-[11px] text-slate-400">{p.paymentDate}</span>
                                )}
                              </div>
                            </div>
                            <span className="font-semibold text-slate-900 shrink-0">
                              ₹{Number(p.paymentAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 pt-3 border-t border-slate-200 mt-3">
                  <button
                    onClick={() => setBillModalOpen(false)}
                    className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium"
                  >
                    Close
                  </button>
                  <div className="ml-auto flex gap-2">
                    {canChangePaymentMode && (
                      <button
                        onClick={() => { setPmStep('mode'); setPmChosenMode(null); setPmAccounts([]); setShowPaymentModeModal(true); }}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium"
                      >
                        <ArrowLeftRight size={16} />
                        <span>Change Payment Mode</span>
                      </button>
                    )}
                    {canReprint && (
                      <button
                        onClick={() => setShowReprintModal(true)}
                        disabled={billItemsLoading || billItems.length === 0}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-lg hover:from-teal-700 hover:to-teal-800 transition-colors text-sm font-bold disabled:opacity-50 shadow-sm"
                      >
                        <Printer size={16} />
                        <span>Reprint</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Mode Change Modal */}
      {showPaymentModeModal && selectedBill && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-5 py-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                {pmStep === 'account' && (
                  <button
                    onClick={() => { setPmStep('mode'); setPmChosenMode(null); setPmAccounts([]); }}
                    disabled={paymentModeUpdating}
                    className="p-1 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <ArrowLeft size={18} className="text-white" />
                  </button>
                )}
                <h3 className="text-base font-bold text-white">
                  {pmStep === 'account' && pmChosenMode
                    ? `Select ${pmChosenMode.label} Account`
                    : 'Change Payment Mode'}
                </h3>
              </div>
              <button onClick={closePaymentModeModal} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                <X size={18} className="text-white" />
              </button>
            </div>

            {/* Step 1: pick the payment mode */}
            {pmStep === 'mode' && (
              <div className="p-4 space-y-2">
                {[
                  { mode: 'CASH', label: 'Cash', icon: Banknote, accountType: null },
                  { mode: 'UPI', label: 'UPI', icon: Smartphone, accountType: 'upi' },
                  { mode: 'CARD', label: 'Card', icon: CreditCard, accountType: 'card_machine' },
                  { mode: 'BANK', label: 'Bank', icon: Building2, accountType: 'bank' },
                ].map((modeDef) => {
                  const { mode, label, icon: Icon } = modeDef;
                  const currentMode = (selectedBill.payment_modes || selectedBill.paymentMode || '').toUpperCase();
                  const isCurrent = currentMode === mode;
                  return (
                    <button
                      key={mode}
                      onClick={() => handleModeTileClick(modeDef)}
                      disabled={paymentModeUpdating}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all text-left ${
                        isCurrent
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'
                      } disabled:opacity-60`}
                    >
                      <Icon size={20} className={isCurrent ? 'text-teal-600' : 'text-slate-500'} />
                      <span className="font-medium flex-1">{label}</span>
                      {isCurrent && (
                        <span className="text-xs font-semibold text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full">Current</span>
                      )}
                      {mode !== 'CASH' && !isCurrent && (
                        <ChevronDown size={16} className="-rotate-90 text-slate-400" />
                      )}
                      {paymentModeUpdating && mode === 'CASH' && (
                        <Loader2 size={16} className="animate-spin text-slate-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Step 2: pick a specific account for the chosen mode */}
            {pmStep === 'account' && (
              <div className="p-4">
                {pmAccountsLoading ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
                    <Loader2 size={18} className="animate-spin" />
                    <span className="text-sm">Loading accounts…</span>
                  </div>
                ) : pmAccounts.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-sm font-medium text-slate-700">
                      No {pmChosenMode?.label} accounts configured
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Add one under Settings → Payment Accounts.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {pmAccounts.map((acc) => {
                      const subtitle = accountSubtitle(acc);
                      return (
                        <button
                          key={acc.id}
                          onClick={() =>
                            handlePaymentModeChange(
                              pmChosenMode.mode,
                              acc.id,
                              acc.accountName || acc.account_name
                            )
                          }
                          disabled={paymentModeUpdating}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-slate-200 hover:border-teal-300 hover:bg-teal-50/40 transition-all text-left text-slate-700 disabled:opacity-60"
                        >
                          <pmChosenMode.icon size={20} className="text-slate-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {acc.accountName || acc.account_name}
                            </p>
                            {subtitle && (
                              <p className="text-xs text-slate-500 truncate">{subtitle}</p>
                            )}
                          </div>
                          {paymentModeUpdating && (
                            <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reprint Receipt Modal */}
      {showReprintModal && selectedBill && (
        <ReprintReceiptModal
          bill={buildBillForReprint()}
          onClose={() => setShowReprintModal(false)}
        />
      )}

      {/* Cancel Bill Confirmation Modal */}
      {cancelConfirmOpen && billToCancel && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-5 py-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Ban size={18} className="text-white" />
                <h3 className="text-base font-bold text-white">Cancel Bill</h3>
              </div>
              <button
                onClick={closeCancelConfirm}
                disabled={cancelling}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
              >
                <X size={18} className="text-white" />
              </button>
            </div>

            <div className="p-5">
              <p className="text-sm text-slate-700">
                Are you sure you want to cancel{' '}
                <span className="font-bold text-slate-900">
                  Bill #{billToCancel.bill_number || billToCancel.billNumber || billToCancel.id}
                </span>
                ?
              </p>

              {/* Final amount summary — quick sanity check before the destructive action */}
              {(() => {
                const amount =
                  billToCancel.finalAmount ??
                  billToCancel.final_amount ??
                  billToCancel.totalAmount ??
                  billToCancel.total_amount;
                if (amount === null || amount === undefined) return null;
                return (
                  <div className="mt-3 flex items-center justify-between bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Final Amount</span>
                    <span className="text-base font-bold text-red-700">
                      ₹{Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                );
              })()}

              {/* Reason — mandatory. The bill row is deleted server-side, so this text is
                  the only surviving explanation of why it went away. */}
              <div className="mt-4">
                <label htmlFor="cancel-bill-reason" className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Reason <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="cancel-bill-reason"
                  rows={3}
                  value={cancelReason}
                  maxLength={500}
                  disabled={cancelling}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. Duplicate bill, customer walked out, wrong table"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-slate-50 disabled:opacity-60"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-[11px] text-slate-400">Recorded in the audit log</span>
                  <span className="text-[11px] text-slate-400">{cancelReason.length}/500</span>
                </div>
              </div>

              <p className="text-xs text-slate-500 mt-3">
                This action cannot be undone. Stock consumed by this bill will be returned to inventory.
              </p>
            </div>

            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={closeCancelConfirm}
                disabled={cancelling}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={confirmCancelBill}
                disabled={cancelling || !cancelReason.trim()}
                title={!cancelReason.trim() ? 'Enter a reason to continue' : undefined}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {cancelling ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                {cancelling ? 'Cancelling...' : 'Cancel Bill'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}